import React from "react"
import {Card, CardBody, CardHeader, Divider} from "@heroui/react"
import {
  IconArchive,
  IconArchiveOff,
  IconCircleDashed,
  IconLock,
  IconMail,
  IconShield,
} from "@tabler/icons-react"

import {MessageFilter} from "../filterTypes"
import {ActionDisplay} from "./ActionDisplay"
import {ConditionsSection} from "./ConditionsSection"
import {getActionText, isArchiveAndSensitive} from "../filterUtils"

// Get action icon based on filter action
const getActionIcon = (action: string, sensitivity: number = 0) => {
  if (isArchiveAndSensitive(action, sensitivity)) {
    return <IconLock className="h-4 w-4" />
  }

  switch (action) {
    case "message/archive":
      return <IconArchive className="h-4 w-4" />
    case "message/no-archive":
      return <IconArchiveOff className="h-4 w-4" />
    default:
      return <IconCircleDashed className="h-4 w-4" />
  }
}

// Get channel icon based on channel type
const getChannelIcon = (channelType: string) => {
  switch (channelType.toLowerCase()) {
    case "email":
      return <IconMail className="h-4 w-4" />
    default:
      return null
  }
}

// Get sensitivity icon
const getSensitivityIcon = (level: number) => {
  if (level <= 0) return null
  return <IconShield className="h-3.5 w-3.5" />
}

// Get action color based on filter action
const getActionColor = (action: string, sensitivity: number = 0) => {
  if (isArchiveAndSensitive(action, sensitivity)) {
    return "bg-secondary-50 text-secondary-500 border-secondary-200"
  }

  switch (action) {
    case "message/archive":
      return "bg-primary-50 text-primary-500 border-primary-200"
    case "message/no-archive":
      return "bg-danger-50 text-danger-500 border-danger-200"
    default:
      return "bg-gray-50 text-gray-600 border-gray-200"
  }
}

// Shortened ID for display
const shortenId = (id: string) => {
  // Format: f1a2b3c4-d5e6-7f8g-9h0i-j1k2l3m4n5o6
  // To: f1a2b3c4-d5e6-7f8g-9h0i
  const parts = id.split("-")
  if (parts.length >= 4) {
    return parts.slice(0, 4).join("-")
  }
  return id
}

// Filter Card component
export const FilterCard = ({filter}: {filter: MessageFilter}) => {
  // Get the appropriate action icon and color
  const actionIcon = getActionIcon(filter.metadata.action, filter.sensitivity)
  const actionColor = getActionColor(filter.metadata.action, filter.sensitivity)
  const channelIcon = getChannelIcon(filter.channel_type)
  const sensitivityIcon = getSensitivityIcon(filter.sensitivity)

  // Action props
  const actionProps = {
    action: filter.metadata.action,
    text: getActionText(filter.metadata.action, filter.sensitivity),
    color: actionColor,
    icon: actionIcon,
  }

  return (
    <Card className="mb-4 border-default-200">
      <CardHeader className="flex items-center justify-between py-3">
        <div className="flex items-center gap-2">
          <div>
            <h3 className="text-lg font-medium">
              {filter.title || "Unnamed Filter"}
            </h3>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="flex gap-2">
            <div className="flex items-center gap-1 rounded-md bg-gray-50 px-3 py-1 text-sm text-gray-700">
              <span className="font-medium">Status:</span>
              <span
                className={
                  filter.status === "active"
                    ? "text-success-600"
                    : "text-gray-500"
                }
              >
                {filter.status}
              </span>
            </div>

            <div className="flex items-center gap-1 rounded-md bg-gray-50 px-3 py-1 text-sm text-gray-700">
              <span className="font-medium">Channel:</span>
              {channelIcon && (
                <span className="text-primary-500">{channelIcon}</span>
              )}
              <span>{filter.channel_type}</span>
            </div>
          </div>
        </div>
      </CardHeader>
      <Divider />
      <CardBody className="py-3">
        <div className="mb-3 flex flex-col gap-1.5">
          <span className="text-xs text-gray-500">
            Filter ID: {shortenId(filter.id)}
          </span>

          <p className="text-sm text-gray-600">{filter.description || ""}</p>
        </div>

        {filter.metadata.action && <ActionDisplay {...actionProps} />}

        <ConditionsSection conditions={filter.metadata.fieldConditions} />
      </CardBody>
    </Card>
  )
}
